// WebRTC peer management for the shared screen (M57.4, ARCH §30.6, COW-9).
// Client-only chrome: the sim never learns a share exists (D-TF-180) — this
// module owns every `RTCPeerConnection`, the server only ever relays opaque
// `Signal` payloads between exactly two peers (§30.7's one exception).
//
// The VIEWER always initiates the offer: `watch()` creates a peer connection
// and sends it as soon as an authorized `ShareState` names the sharer, so the
// sharer never needs a standing list of who MIGHT watch — it only reacts to
// an incoming offer by creating a matching peer connection for that one
// viewer id. This mirrors the server's own `ShareJoin`-triggers-authorization
// flow exactly (COW-7).
import type { NetSession } from './client';
import { COW } from '../../sim/config';

export type ViewerState = 'connecting' | 'live' | 'relayed' | 'failed';

interface SignalEnvelope {
  type: 'offer' | 'answer' | 'ice';
  sdp?: string;
  candidate?: RTCIceCandidateInit;
}

/** One peer connection's chrome-side bookkeeping (sharer's per-viewer map, or
 *  the viewer's own single connection). */
interface Peer {
  pc: RTCPeerConnection;
  state: ViewerState;
}

export class ShareController {
  private readonly net: NetSession;
  private readonly myId: () => number;
  /** Sharer side: one connection per current viewer. Empty when not sharing. */
  private readonly viewerPeers = new Map<number, Peer>();
  private localStream: MediaStream | null = null;
  /** Viewer side: the one connection to whoever is sharing, or null. */
  private watchPeer: Peer | null = null;
  private watchSharerId = 0;
  remoteStream: MediaStream | null = null;
  onRemoteStream: (() => void) | null = null;
  onViewerStateChanged: (() => void) | null = null;

  constructor(net: NetSession, myId: () => number) {
    this.net = net;
    this.myId = myId;
    this.net.onSignal = (from, to, payload) => this.handleSignal(from, to, payload);
  }

  /** Sharer: begin offering `stream` — actual authorization/broadcast already
   *  happened server-side via `ShareStart`; this just readies the local track
   *  so an incoming viewer offer has something to answer with. */
  share(stream: MediaStream): void {
    this.localStream = stream;
  }

  /** Sharer: stop sharing — close every viewer connection. */
  stopSharing(): void {
    for (const p of this.viewerPeers.values()) p.pc.close();
    this.viewerPeers.clear();
    this.localStream = null;
  }

  /** Viewer: watch `sharerId`'s screen, offering our own connection first. */
  watch(sharerId: number, ice: RTCIceServer[] | undefined): void {
    this.unwatch();
    this.watchSharerId = sharerId;
    const pc = new RTCPeerConnection(ice ? { iceServers: ice } : undefined);
    const peer: Peer = { pc, state: 'connecting' };
    this.watchPeer = peer;
    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.ontrack = (e) => {
      this.remoteStream = e.streams[0] ?? new MediaStream([e.track]);
      this.onRemoteStream?.();
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) this.net.sendSignal(this.myId(), sharerId, JSON.stringify({ type: 'ice', candidate: e.candidate.toJSON() } satisfies SignalEnvelope));
    };
    pc.onconnectionstatechange = () => this.setState(peer, pc.connectionState);
    pc.createOffer().then((offer) => pc.setLocalDescription(offer)).then(() => {
      this.net.sendSignal(this.myId(), sharerId, JSON.stringify({ type: 'offer', sdp: pc.localDescription!.sdp } satisfies SignalEnvelope));
    });
  }

  unwatch(): void {
    this.watchPeer?.pc.close();
    this.watchPeer = null;
    this.remoteStream = null;
    this.watchSharerId = 0;
  }

  watchState(): ViewerState | null {
    return this.watchPeer?.state ?? null;
  }

  /** Test-only (E-64 seam): a headless CI browser has no way to make ICE
   *  genuinely fail, so `voxel.cow.fakeShare`'s test partner forces it
   *  directly — closing the connection and marking it `failed`, exactly the
   *  state a real ICE failure would leave it in. */
  forceWatchFail(): void {
    if (!this.watchPeer) return;
    this.watchPeer.pc.close();
    this.watchPeer.state = 'failed';
    this.onViewerStateChanged?.();
  }

  /** Sharer's per-viewer connection state, for the viewer-list HUD (§30.6). */
  viewers(): Array<{ id: number; state: ViewerState }> {
    return [...this.viewerPeers.entries()].map(([id, p]) => ({ id, state: p.state }));
  }

  private setState(peer: Peer, cs: RTCPeerConnectionState): void {
    const next: ViewerState = cs === 'connected' ? (peer.state === 'relayed' ? 'relayed' : 'live')
      : cs === 'failed' ? 'failed'
      : 'connecting';
    if (next !== peer.state) { peer.state = next; this.onViewerStateChanged?.(); }
  }

  private async handleSignal(from: number, to: number, raw: string): Promise<void> {
    if (to !== this.myId()) return;
    let env: SignalEnvelope;
    try { env = JSON.parse(raw) as SignalEnvelope; } catch { return; }

    if (env.type === 'offer') {
      // We are the SHARER, `from` is a new (or reconnecting) viewer.
      if (!this.localStream) return;
      this.viewerPeers.get(from)?.pc.close();
      const pc = new RTCPeerConnection();
      const peer: Peer = { pc, state: 'connecting' };
      this.viewerPeers.set(from, peer);
      for (const track of this.localStream.getTracks()) pc.addTrack(track, this.localStream);
      pc.onicecandidate = (e) => {
        if (e.candidate) this.net.sendSignal(this.myId(), from, JSON.stringify({ type: 'ice', candidate: e.candidate.toJSON() } satisfies SignalEnvelope));
      };
      pc.onconnectionstatechange = () => { this.setState(peer, pc.connectionState); if (pc.connectionState === 'closed' || pc.connectionState === 'failed') this.viewerPeers.delete(from); };
      await pc.setRemoteDescription({ type: 'offer', sdp: env.sdp });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      this.net.sendSignal(this.myId(), from, JSON.stringify({ type: 'answer', sdp: pc.localDescription!.sdp } satisfies SignalEnvelope));
    } else if (env.type === 'answer') {
      // We are a VIEWER; `from` must be who we're watching.
      if (from !== this.watchSharerId || !this.watchPeer) return;
      await this.watchPeer.pc.setRemoteDescription({ type: 'answer', sdp: env.sdp });
    } else if (env.type === 'ice' && env.candidate) {
      const peer = from === this.watchSharerId ? this.watchPeer : this.viewerPeers.get(from);
      await peer?.pc.addIceCandidate(env.candidate).catch(() => {});
    }
  }

  dispose(): void {
    this.stopSharing();
    this.unwatch();
    this.net.onSignal = null;
  }
}

/** The capture constraints for a real (non-`fakeShare`) share (COW-8, COW-12):
 *  VP8/text-hint/no-audio, and this is the ONLY place the client ever asks
 *  for display media — never `getUserMedia` (voice is a named non-goal,
 *  D-TF-178). */
export function captureConstraints(): DisplayMediaStreamOptions {
  return {
    video: { width: COW.VIDEO.w, height: COW.VIDEO.h, frameRate: COW.VIDEO.fps },
    audio: false,
  };
}
